import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';

import { SyntheticEvent, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import {
  borderRadius,
  fontSize,
  fontWeight,
  isMediaMax,
  mediaMax,
  mediaMin,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import {
  black,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import { homePath } from 'presentations/routes/path';
import { setEvent } from 'presentations/views/helpers/analytics';
import Image from 'presentations/views/components/atoms/Image';
import { ProductType } from 'domains/models/blog_post/productType';
import {
  getPersistentItem,
  setPersistentItem,
} from 'presentations/views/helpers/storage';
import { imagePath } from 'presentations/assets';
import { getLandingPageUrl } from '..';
import { Amplitude } from 'presentations/views/helpers/analytics/amplitude';

interface Props {
  productType: ProductType;
  position?: string;
  isHideClose?: boolean;
  className?: string;
  adId?: string | null;
  name?: string;
}

export default function (props: Props) {
  const t = translateInComponent(translations);
  // const cacheKey = 'resources/footer/banner/20220104';
  // const cacheExp = useMemo(() => 60 * 60 * 24 * 7, []);
  // const cachedIsShow = getPersistentItem(cacheKey);
  // const defaultIsShow =
  //   cachedIsShow === null || cachedIsShow === undefined ? true : cachedIsShow;
  // const [isShow, setIsShow] = useState<boolean>(defaultIsShow);
  const [isShow, setIsShow] = useState<boolean>(true);
  const isMobile = isMediaMax('tablet');

  const targetUrl = useMemo(() => {
    return getLandingPageUrl(props.productType);
  }, [props.productType]);
  const eventName = 'click resource banner';

  const onClick = (e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setEvent(eventName, {
      position: 'footer',
    });
    Amplitude.magazineAdClick({
      position: 'footer',
      contentType: 'top',
      contentId: 'default',
      productType: props.productType,
    });
    window.open(targetUrl);
  };

  const onClickClose = (e: SyntheticEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // setIsShow(false);
    // setPersistentItem(cacheKey, false, cacheExp);
    // setEvent(`${eventName} close`, {
    //   position: 'footer',
    // });
  };

  if (!isShow) {
    return null;
  }

  return (
    <FooterAdContainer
      className={props.className}
      href={targetUrl}
      onClick={onClick}
    >
      <FooterAdBackgroundImage gridColor={'white'} />
      <FooterMain>
        <FooterAdLogo>
          <Image
            src="logo/www_white.svg"
            alt={t('Jicoo')}
            width={160}
            height={40}
          />
        </FooterAdLogo>
        <FooterAdLogoTablet>
          <Image
            src="ads/logo_vert_white.svg"
            alt={t('Jicoo')}
            width={45}
            height={49}
          />
        </FooterAdLogoTablet>
        <FooterAdRow>
          <FooterAdTitle>
            <LinkText>{t(`title_${props.productType}`)}</LinkText>
          </FooterAdTitle>
          <FooterAdDescription>
            <LinkText>
              <ShortText>
                {t(`description_${props.productType}_short`)}
              </ShortText>
              <LongText>{t(`description_${props.productType}`)}</LongText>
            </LinkText>
          </FooterAdDescription>
        </FooterAdRow>
        <FooterRight>
          <FooterButtonContainer>
            <FooterAdLinkPrefix>{t('for free')}</FooterAdLinkPrefix>
            <FooterAdLink>
              {t(isMobile ? 'try it for free' : 'get started')}
              <FooterAdLinkIcon name="chevron-right" />
            </FooterAdLink>
          </FooterButtonContainer>
        </FooterRight>
      </FooterMain>
      {!props.isHideClose && (
        <FooterCloseIconContainer onClick={onClickClose}>
          <FooterCloseIcon name="close" />
        </FooterCloseIconContainer>
      )}
    </FooterAdContainer>
  );
}

const FooterAdContainer = styled.a`
  display: block;
  position: relative;
  height: 100px;
  background: ${primary};
  color: ${whiteFix};
  ${mediaMax.tablet} {
    height: 72px;
    width: 100%;
  }
`;
const FooterMain = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const FooterAdBackgroundImage = styled.div<{ gridColor: string }>`
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-image: url(${(props) =>
    imagePath(`ads/bg_line_${props.gridColor}.svg`)});
  background-repeat: repeat;
  opacity: 0.1;
`;
const FooterAdTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  color: ${white};
  font-size: ${fontSize(28)};
  ${mediaMax.desktop} {
    font-size: ${fontSize(22)};
  }
  ${mediaMax.tablet} {
    line-height: 1;
    font-size: ${fontSize(14)};
  }
`;
const FooterAdDescription = styled.div`
  font-weight: ${fontWeight('bold')};
  color: ${white};
  margin-top: ${spacing(2)};
  ${mediaMax.desktop} {
    font-size: ${fontSize(13)};
  }
  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
    font-weight: ${fontWeight('normal')};
    letter-spacing: -0.01em;
    text-overflow: ellipsis;
    overflow: hidden;
    max-height: 40px;
  }
`;
const FooterAdLogo = styled.div`
  display: block;
  margin-left: ${spacing(12)};
  ${mediaMax.tablet} {
    display: none;
  }
`;
const FooterAdLogoTablet = styled.div`
  display: block;
  margin-left: ${spacing(8)};
  margin-right: ${spacing(8)};
  ${mediaMin.tablet} {
    display: none;
  }
`;
const FooterAdRow = styled.div`
  margin-left: ${spacing(32)};
  margin-right: ${spacing(32)};
  ${mediaMax.tablet} {
    flex: 1;
    margin-left: 0;
    margin-right: ${spacing(4)};
  }
`;
const FooterButtonContainer = styled.div`
  margin-right: ${spacing(32)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
  }
`;
const FooterAdLinkPrefix = styled.div`
  margin-bottom: ${spacing(4)};
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    display: none;
  }
`;
const FooterAdLink = styled.div`
  display: flex;
  align-items: center;
  color: ${white};
  border-radius: ${borderRadius(6)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  white-space: nowrap;
  cursor: pointer;
  padding: ${spacing(4)} ${spacing(8)} ${spacing(4)} ${spacing(16)};
  background-color: ${white};
  color: ${primary};
  ${mediaMax.desktop} {
    font-size: ${fontSize(14)};
  }
  ${mediaMax.tablet} {
    padding: ${spacing(4)} ${spacing(6)};
    font-size: ${fontSize(13)};
  }
`;
const FooterAdLinkIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${primary};
  margin-left: ${spacing(4)};
  ${mediaMax.tablet} {
    display: none;
  }
`;

const FooterCloseIconContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  width: 28px;
  height: 28px;
  z-index: ${zIndex(100)};
  background-color: ${black};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${mediaMax.tablet} {
    background-color: transparent;
    width: auto;
    height: auto;
  }
`;
const FooterCloseIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${white};
`;
const LinkText = styled.span`
  color: ${whiteFix};
`;

const FooterRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${spacing(4)};
`;

const LongText = styled.div`
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ShortText = styled.div`
  ${mediaMin.tablet} {
    display: none;
  }
`;
